/**
 * Firebase Debug Utilities
 * Diagnostic tools for troubleshooting Firebase authentication issues
 */

import { auth } from '@/config/firebase';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword } from 'firebase/auth';

export interface FirebaseDebugInfo {
  environment: string;
  firebaseConfig: {
    apiKey: string;
    authDomain: string;
    projectId: string;
    storageBucket: string;
    messagingSenderId: string;
    appId: string;
  };
  authState: {
    currentUser: any;
    isConnected: boolean;
    lastError: string | null;
  };
  networkTest: {
    canReachFirebase: boolean;
    canReachAuthDomain: boolean;
    error: string | null;
  };
}

/**
 * Get comprehensive Firebase debug information
 */
export async function getFirebaseDebugInfo(): Promise<FirebaseDebugInfo> {
  const debugInfo: FirebaseDebugInfo = {
    environment: import.meta.env.MODE || 'unknown',
    firebaseConfig: {
      apiKey: import.meta.env.VITE_FIREBASE_API_KEY || 'NOT_SET',
      authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || 'NOT_SET',
      projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || 'NOT_SET',
      storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || 'NOT_SET',
      messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || 'NOT_SET',
      appId: import.meta.env.VITE_FIREBASE_APP_ID || 'NOT_SET',
    },
    authState: {
      currentUser: auth.currentUser ? {
        uid: auth.currentUser.uid,
        email: auth.currentUser.email,
        emailVerified: auth.currentUser.emailVerified,
      } : null,
      isConnected: false,
      lastError: null,
    },
    networkTest: {
      canReachFirebase: false,
      canReachAuthDomain: false,
      error: null,
    }
  };

  // Test Firebase connectivity with improved error handling
  try {
    // Test if we can reach Firebase Auth domain with no-cors mode
    // Use GET instead of HEAD as some servers handle HEAD requests differently
    const response = await fetch(`https://${debugInfo.firebaseConfig.authDomain}/__/auth/handler`, {
      method: 'GET',
      mode: 'no-cors',
      cache: 'no-cache'
    });
    // In no-cors mode, we can't read the status, but if no error is thrown, it's reachable
    debugInfo.networkTest.canReachAuthDomain = true;
  } catch (error) {
    debugInfo.networkTest.canReachAuthDomain = false;
    debugInfo.networkTest.error = `Auth domain test failed (CORS restrictions may apply): ${error instanceof Error ? error.message : 'Unknown network error'}`;
  }

  try {
    // Test if we can reach Firebase API
    const response = await fetch(`https://identitytoolkit.googleapis.com/v1/accounts:lookup?key=${debugInfo.firebaseConfig.apiKey}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ idToken: 'test' }),
      cache: 'no-cache'
    });
    // 400 is expected for invalid test data - it means API is reachable and responding
    debugInfo.networkTest.canReachFirebase = response.status >= 200 && response.status < 500;
  } catch (error) {
    debugInfo.networkTest.canReachFirebase = false;
    if (!debugInfo.networkTest.error) {
      debugInfo.networkTest.error = error instanceof Error ? error.message : 'Unknown Firebase API error';
    } else {
      debugInfo.networkTest.error += ` | Firebase API error: ${error instanceof Error ? error.message : 'Unknown'}`;
    }
  }

  return debugInfo;
}

/**
 * Test Firebase authentication with test credentials
 */
export async function testFirebaseAuth(email: string, password: string): Promise<{
  success: boolean;
  error: string | null;
  errorCode: string | null;
  details: any;
}> {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return {
      success: true,
      error: null,
      errorCode: null,
      details: {
        uid: userCredential.user.uid,
        email: userCredential.user.email,
        emailVerified: userCredential.user.emailVerified,
      }
    };
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Unknown authentication error',
      errorCode: error.code || 'unknown',
      details: {
        stack: error.stack,
        customData: error.customData,
      }
    };
  }
}

/**
 * Validate Firebase configuration
 */
export function validateFirebaseConfig(): {
  isValid: boolean;
  missingFields: string[];
  invalidFields: string[];
  warnings: string[];
} {
  const config = {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
  };

  const missingFields: string[] = [];
  const invalidFields: string[] = [];
  const warnings: string[] = [];

  // Check for missing fields
  Object.entries(config).forEach(([key, value]) => {
    if (!value || value === 'NOT_SET' || value === 'your_' + key.toLowerCase() + '_here') {
      missingFields.push(key);
    }
  });

  // Check for invalid patterns
  if (config.apiKey && !config.apiKey.startsWith('AIza')) {
    invalidFields.push('apiKey - should start with "AIza"');
  }

  if (config.authDomain && !config.authDomain.includes('.firebaseapp.com')) {
    invalidFields.push('authDomain - should end with ".firebaseapp.com"');
  }

  if (config.projectId && config.authDomain && !config.authDomain.startsWith(config.projectId)) {
    warnings.push('authDomain should typically start with projectId');
  }

  if (config.appId && !config.appId.includes(':')) {
    invalidFields.push('appId - should contain colons');
  }

  // Check for demo/example values
  if (config.projectId === 'demo-project' || config.projectId === 'your_project_id') {
    warnings.push('Using demo/example project ID');
  }

  return {
    isValid: missingFields.length === 0 && invalidFields.length === 0,
    missingFields,
    invalidFields,
    warnings,
  };
}

/**
 * Generate Firebase configuration troubleshooting report
 */
export async function generateTroubleshootingReport(): Promise<string> {
  const debugInfo = await getFirebaseDebugInfo();
  const configValidation = validateFirebaseConfig();

  let report = `# Firebase Authentication Troubleshooting Report\n\n`;
  report += `**Generated:** ${new Date().toISOString()}\n`;
  report += `**Environment:** ${debugInfo.environment}\n\n`;

  // Configuration Status
  report += `## Configuration Status\n\n`;
  if (configValidation.isValid) {
    report += `✅ Firebase configuration appears valid\n\n`;
  } else {
    report += `❌ Firebase configuration has issues\n\n`;
  }

  if (configValidation.missingFields.length > 0) {
    report += `**Missing Fields:**\n`;
    configValidation.missingFields.forEach(field => {
      report += `- ${field}\n`;
    });
    report += `\n`;
  }

  if (configValidation.invalidFields.length > 0) {
    report += `**Invalid Fields:**\n`;
    configValidation.invalidFields.forEach(field => {
      report += `- ${field}\n`;
    });
    report += `\n`;
  }

  if (configValidation.warnings.length > 0) {
    report += `**Warnings:**\n`;
    configValidation.warnings.forEach(warning => {
      report += `- ${warning}\n`;
    });
    report += `\n`;
  }

  // Network Connectivity
  report += `## Network Connectivity\n\n`;
  report += `**Can reach Auth Domain:** ${debugInfo.networkTest.canReachAuthDomain ? '✅' : '❌'}\n`;
  report += `**Can reach Firebase API:** ${debugInfo.networkTest.canReachFirebase ? '✅' : '❌'}\n`;
  if (debugInfo.networkTest.error) {
    report += `**Network Error:** ${debugInfo.networkTest.error}\n`;
  }
  report += `\n`;

  // Current Auth State
  report += `## Authentication State\n\n`;
  if (debugInfo.authState.currentUser) {
    report += `**Current User:** ${debugInfo.authState.currentUser.email} (${debugInfo.authState.currentUser.uid})\n`;
    report += `**Email Verified:** ${debugInfo.authState.currentUser.emailVerified ? '✅' : '❌'}\n`;
  } else {
    report += `**Current User:** None (not signed in)\n`;
  }
  report += `\n`;

  // Configuration Details (masked for security)
  report += `## Configuration Details\n\n`;
  report += `**API Key:** ${debugInfo.firebaseConfig.apiKey.substring(0, 10)}...\n`;
  report += `**Auth Domain:** ${debugInfo.firebaseConfig.authDomain}\n`;
  report += `**Project ID:** ${debugInfo.firebaseConfig.projectId}\n`;
  report += `**Storage Bucket:** ${debugInfo.firebaseConfig.storageBucket}\n`;
  report += `**Messaging Sender ID:** ${debugInfo.firebaseConfig.messagingSenderId}\n`;
  report += `**App ID:** ${debugInfo.firebaseConfig.appId.substring(0, 15)}...\n\n`;

  // Recommendations
  report += `## Recommendations\n\n`;
  
  if (!configValidation.isValid) {
    report += `1. **Fix Configuration Issues:** Update your .env file with correct Firebase configuration values\n`;
    report += `2. **Check Firebase Console:** Verify your project settings in the Firebase Console\n`;
    report += `3. **Regenerate Config:** If needed, regenerate your web app configuration in Firebase Console\n`;
  }

  if (!debugInfo.networkTest.canReachFirebase || !debugInfo.networkTest.canReachAuthDomain) {
    report += `4. **Check Network:** Verify internet connectivity and firewall settings\n`;
    report += `5. **Check CORS:** Ensure your domain is authorized in Firebase Console\n`;
  }

  if (debugInfo.environment === 'development') {
    report += `6. **Development Mode:** Consider using Firebase emulators for local development\n`;
  }

  return report;
}

// Global debug functions for browser console
declare global {
  interface Window {
    debugFirebaseAuth: () => Promise<void>;
    testFirebaseLogin: (email: string, password: string) => Promise<void>;
    generateFirebaseReport: () => Promise<void>;
  }
}

// Add debug functions to window for browser console access
if (typeof window !== 'undefined') {
  window.debugFirebaseAuth = async () => {
    const debugInfo = await getFirebaseDebugInfo();
    console.log('Firebase Debug Info:', debugInfo);
  };

  window.testFirebaseLogin = async (email: string, password: string) => {
    const result = await testFirebaseAuth(email, password);
    console.log('Firebase Auth Test Result:', result);
  };

  window.generateFirebaseReport = async () => {
    const report = await generateTroubleshootingReport();
    console.log(report);
  };
}
